import React from 'react'
import { Platform } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { FIELD_TEXT_CLASS } from '../../fieldTypography'
import { Popover, PopoverBackdrop } from '../popover'
import { Text } from '../text'
import { DropdownOptions } from './DropdownOptions'
import { DropdownTrigger } from './DropdownTrigger'
import { DropdownProps } from './types'
import { hasDropdownValue, useDropdown } from './useDropdown'
import { useDropdownPlacement } from './useDropdownPlacement'

const containsNode = (root: unknown, target: EventTarget | null) =>
    !!root && !!target && (root as HTMLElement).contains?.(target as Node)

/**
 * Web closes on an outside pointer-down instead of a full-screen backdrop: the
 * backdrop overlays the trigger too, so a second click into the search input
 * landed on the backdrop and blurred the field mid-typing.
 */
const useCloseOnOutsidePress = (
    isOpen: boolean,
    triggerRef: React.MutableRefObject<unknown>,
    contentRef: React.MutableRefObject<unknown>,
    close: () => void,
) => {
    React.useEffect(() => {
        if (Platform.OS !== 'web' || !isOpen) {
            return undefined
        }
        const onPointerDown = (event: MouseEvent | TouchEvent) => {
            if (
                containsNode(triggerRef.current, event.target) ||
                containsNode(contentRef.current, event.target)
            ) {
                return
            }
            close()
        }
        document.addEventListener('mousedown', onPointerDown, true)
        document.addEventListener('touchstart', onPointerDown, true)
        return () => {
            document.removeEventListener('mousedown', onPointerDown, true)
            document.removeEventListener('touchstart', onPointerDown, true)
        }
    }, [isOpen, triggerRef, contentRef, close])
}

export type { DropdownProps } from './types'

export function Dropdown<T>({
    items,
    value,
    labelRenderer,
    valueRenderer,
    itemRenderer,
    onChange,
    onSearch,
    createItem,
    searchable = true,
    placeholder,
    searchPlaceholder,
    emptyText,
    loadingText,
    errorText,
    disabled = false,
    isInvalid,
    hasClear = true,
    boundary,
    className,
    nativeID,
    testID,
}: DropdownProps<T>) {
    const {
        isOpen,
        setIsOpen,
        setQuery,
        triggerWidth,
        setTriggerWidth,
        filtered,
        selectedLabel,
        fieldValue,
        isLoading,
        isError,
        close,
        toggle,
        select,
        clear,
    } = useDropdown({
        items,
        value,
        labelRenderer,
        onChange,
        onSearch,
        createItem,
    })

    const triggerNodeRef = React.useRef<unknown>(null)
    const contentNodeRef = React.useRef<unknown>(null)
    useCloseOnOutsidePress(isOpen && !disabled, triggerNodeRef, contentNodeRef, close)
    const { placement, maxHeight } = useDropdownPlacement(
        isOpen && !disabled,
        triggerNodeRef,
        boundary,
    )

    const setTriggerNode = React.useCallback((node: unknown) => {
        triggerNodeRef.current = node
    }, [])
    const setContentNode = React.useCallback((node: unknown) => {
        contentNodeRef.current = node
    }, [])

    const valueNode = hasDropdownValue(value) ? (
        valueRenderer ? (
            valueRenderer(value)
        ) : (
            <Text
                size='body'
                numberOfLines={1}
                isTruncated
                className={twMerge('min-w-0 flex-1', FIELD_TEXT_CLASS)}
            >
                {selectedLabel}
            </Text>
        )
    ) : undefined

    const placeholderLabel = placeholder ?? searchPlaceholder

    const staticNode = searchable
        ? isOpen
            ? undefined
            : valueNode
        : (valueNode ?? (
              <Text
                  size='body'
                  tone='muted'
                  numberOfLines={1}
                  isTruncated
                  className={twMerge('min-w-0 flex-1', FIELD_TEXT_CLASS)}
              >
                  {placeholderLabel}
              </Text>
          ))

    return (
        <Popover
            isOpen={isOpen && !disabled}
            onClose={close}
            trapFocus={false}
            focusScope={false}
            placement={placement}
            trigger={triggerProps => (
                <DropdownTrigger
                    triggerProps={triggerProps as { ref?: React.Ref<unknown> }}
                    innerRef={setTriggerNode}
                    value={staticNode ? '' : fieldValue}
                    valueNode={staticNode}
                    valueLabel={staticNode ? (selectedLabel ?? placeholderLabel) : undefined}
                    placeholder={
                        staticNode
                            ? ''
                            : isOpen
                              ? (searchPlaceholder ?? selectedLabel ?? placeholder)
                              : placeholderLabel
                    }
                    disabled={disabled}
                    isInvalid={isInvalid}
                    isOpen={isOpen && !disabled}
                    showClear={hasClear && !!selectedLabel}
                    className={className}
                    nativeID={nativeID}
                    testID={testID}
                    onFocus={() => setIsOpen(true)}
                    onChangeText={
                        searchable
                            ? text => {
                                  setQuery(text)
                                  setIsOpen(true)
                              }
                            : undefined
                    }
                    onSubmit={createItem ? close : undefined}
                    onClear={clear}
                    onToggle={toggle}
                    onMeasureWidth={setTriggerWidth}
                />
            )}
        >
            {Platform.OS !== 'web' && <PopoverBackdrop />}
            <DropdownOptions
                contentRef={setContentNode}
                items={filtered}
                selectedLabel={selectedLabel}
                labelRenderer={labelRenderer}
                valueRenderer={itemRenderer ?? valueRenderer}
                onSelect={select}
                emptyText={emptyText}
                loading={isLoading}
                loadingText={loadingText}
                error={isError}
                errorText={errorText}
                width={triggerWidth}
                maxHeight={maxHeight}
            />
        </Popover>
    )
}

Dropdown.displayName = 'Dropdown'
